import React from 'react';
import CombatTooltip from '../GameTip/CombatTooltip';
import { VasodilationPill } from '../StatusTags/KcStatusPills';
import { isCombatTerminalPhase } from './playerbarPhase';

// Vasodilation (2026-07-28) — the HTN regimen's defensive coupon. A charge voids ONE
// WHOLE pressure application from an enemy, whatever that application's size: Angio's
// +1 and Natrix's +2 each cost exactly one charge. Charges pool across cards and never
// regenerate inside a fight, so the tag is a countdown, not a resource bar.
//
// Vasodilating cards: dihydropyridine CCBs (3 charges each, the direct arteriolar
// dilators) and the alpha-beta blockers carvedilol + labetalol (2 each, dilating
// through alpha-1 blockade alongside their beta effect). Nebivolol is deliberately
// excluded — its vasodilation is nitric-oxide mediated, not alpha blockade.
//
// Blue rgb(96,165,250) is the app's established "the incoming thing was negated"
// colour (the ADR-dodge absorb chip). Vasodilation is that primitive, so it joins the
// family rather than inventing an accent. It is NOT pressure red: this tag is the
// answer to pressure, not a dose of it.

const vasodilationIcon = (size, className) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* A vessel bowing OPEN — two walls bulging apart across the middle. */}
    <path d="M3 7c4 0 4.5-2.5 9-2.5S17 7 21 7" />
    <path d="M3 17c4 0 4.5 2.5 9 2.5s5-2.5 9-2.5" />
  </svg>
);

export const VASODILATION_ACCENT = 'rgb(96, 165, 250)';

/**
 * THE Vasodilation tooltip. Returns CombatTooltip props (everything but children), so
 * the combat rail and the drug toast render one identical panel from one definition
 * instead of each reconstructing it. Same one-builder-two-mounts contract as
 * `htnPulseTooltipProps` / `htnPotassiumTooltipProps` / `htnDiuresisTooltipProps`.
 *
 * `remaining` is what is LEFT (the combat rail). `initial` is what the loadout
 * granted. The drug toast passes only `initial`, because a card describes what it
 * grants, not the state of a fight it is not in.
 */
export function htnVasodilationTooltipProps({
  remaining = null,
  initial = null,
  sourceNames = [],
} = {}) {
  // ⛔ Test for null/undefined BEFORE coercing. `Number(null)` is 0, not NaN, so a
  // bare isFinite check reads "absent" as "zero" — which made a card's tooltip claim
  // all its charges were already spent.
  const asCount = (value) => (
    value === null || value === undefined || !Number.isFinite(Number(value))
      ? null
      : Math.max(0, Math.trunc(Number(value)))
  );
  const left = asCount(remaining);
  const granted = asCount(initial);
  const isLive = left !== null;
  const spent = isLive && granted !== null ? Math.max(0, granted - left) : null;

  const lead = 'Widened vessels absorb an enemy pressure application before it lands.';
  // The magnitude-independence is the whole point of the mechanic and the single
  // thing a player is most likely to guess wrong, so it is stated outright.
  const rule = 'One charge voids one whole application, however big it is.';
  const stateLine = isLive
    ? (left > 0
      ? `${left} charge${left === 1 ? '' : 's'} left${spent ? ` (${spent} spent)` : ''}.`
      : 'All charges spent. The next pressure application lands in full.')
    : (granted !== null
      ? `Grants ${granted} charge${granted === 1 ? '' : 's'}.`
      : '');
  const sourceLine = sourceNames.length
    ? `From ${sourceNames.join(', ')}.`
    : '';

  // ⛔ NOT `inline`. CombatTooltip's inline variant renders ONLY the first section
  // line by design (CombatTooltip.jsx:72 — it is a one-row "NAME: def" collapse), so
  // an inline build here would silently drop the rule, the charge count and the
  // sources while still looking like it worked. This uses the full split-header
  // grammar, the same shape the pulse and potassium tags render.
  const subtitle = isLive
    ? (left > 0 ? `${left} of ${granted ?? left} left` : 'Spent')
    : (granted !== null ? `${granted} charge${granted === 1 ? '' : 's'}` : '');

  return {
    color: VASODILATION_ACCENT,
    name: 'Vasodilation',
    subtitle,
    placement: 'above-left',
    icon: vasodilationIcon(18, 'combat-tt__node-icon'),
    sections: [{ lines: [lead, rule, stateLine, sourceLine].filter(Boolean) }],
  };
}

function PlayerVasodilationTag({
  remaining = 0,
  initial = 0,
  sourceNames = [],
  visible = false,
  phase,
}) {
  const left = Math.max(0, Math.trunc(Number(remaining) || 0));
  const granted = Math.max(0, Math.trunc(Number(initial) || 0));
  // The tag persists at 0 rather than disappearing: "you had this and it is gone" is
  // exactly what a player needs in order to read the round that just hurt them. It is
  // hidden only when the loadout never granted any charges at all.
  if (!visible || isCombatTerminalPhase(phase) || granted <= 0) {
    return null;
  }
  return (
    <CombatTooltip {...htnVasodilationTooltipProps({ remaining: left, initial: granted, sourceNames })}>
      <VasodilationPill
        charges={left}
        className={left <= 0 ? 'is-spent' : ''}
        aria-label={left > 0
          ? `Vasodilation, ${left} of ${granted} charge${granted === 1 ? '' : 's'} left`
          : 'Vasodilation, all charges spent'}
      />
    </CombatTooltip>
  );
}

export default PlayerVasodilationTag;
